import SystemConfig, { CONFIG_PARAMS } from '../../system/SystemConfig'
import IUIMessages from '../messages/IUIMessages'
import MadmaxMessage from '../messages/MadmaxMessage'
import { MESSAGE_EVENTS, MESSAGE_TYPES } from '../../error/errorMessages'
import { translate } from '../../translations/translations'
import { BASE_ELEMENT_IDS } from '../BaseDOM'
import events from '../../events/EventsObservable'
import { EVENTS } from '../../events/baseEvents'

/**
 * Warning after max bet is set, driven by settings max_bet_warning.level:
 * 1 - toast hiding after timeout, bet change or spin
 * 2 - toast with OK button, spin/autostart blocked until OK, timeout or bet change
 *     (without block callback falls back to message closing after timeout)
 * 3+ - message closed by OK (opened message locks all actions)
 * Toast is placed above the anchor (top center in client coordinates), or bottom center without it.
 */
export default class MaxBetWarning {
	config: SystemConfig
	uiMessages: IUIMessages
	block: ((blocked: boolean) => void) | undefined
	getAnchor: (() => { x: number; y: number } | undefined) | undefined

	toast: HTMLElement | undefined = undefined
	toastText: HTMLElement
	toastOk: HTMLButtonElement
	toastProgress: HTMLElement
	toastTimeout: NodeJS.Timeout | undefined = undefined
	blocked: boolean = false

	constructor(
		config: SystemConfig,
		uiMessages: IUIMessages,
		block?: (blocked: boolean) => void,
		getAnchor?: () => { x: number; y: number } | undefined
	) {
		this.config = config
		this.uiMessages = uiMessages
		this.block = block
		this.getAnchor = getAnchor

		events.subscribe(EVENTS.BEFORE_SPIN, () => this.hideToast())
	}

	show(): void {
		const level = this.config.get(CONFIG_PARAMS.MAX_BET_WARNING)
		if (!level) return
		const timeout = this.config.get(CONFIG_PARAMS.MAX_BET_WARNING_TIMEOUT)
		const lang = this.config.get(CONFIG_PARAMS.LANGUAGE)
		const text = translate('max_bet_set', lang) || 'Max bet set'
		if (level === 1 || (level === 2 && this.block)) {
			this.showToast(text, timeout, level === 2)
			return
		}
		const msg = new MadmaxMessage(
			'',
			text,
			{ ok: { event: MESSAGE_EVENTS.MESSAGE_CLOSE } },
			lang,
			MESSAGE_TYPES.WARNING,
			100,
			this.config
		)
		let closeTimeout: NodeJS.Timeout | undefined = undefined
		msg.create()
		this.uiMessages.addMessage(msg)
		msg.setOnClose(() => {
			clearTimeout(closeTimeout)
			msg.destroy()
		})
		msg.openMessage()
		if (level === 2) closeTimeout = setTimeout(() => msg._close(), timeout)
	}

	createToast(): void {
		this.toast = document.createElement('div')
		this.toast.classList.add('mx-maxbet-warning')
		const icon = document.createElement('span')
		icon.classList.add('mx-maxbet-warning-icon')
		icon.innerHTML =
			'<svg viewBox="0 0 24 24"><path d="M12 2.5 1.5 21h21L12 2.5Zm-1 7h2v6h-2v-6Zm0 8h2v2h-2v-2Z" fill="currentColor"/></svg>'
		this.toastText = document.createElement('span')
		this.toastText.classList.add('mx-maxbet-warning-text')
		this.toastOk = document.createElement('button')
		this.toastOk.classList.add('mx-maxbet-warning-ok')
		this.toastOk.innerText = 'OK'
		this.toastOk.addEventListener('click', (e) => {
			e.stopPropagation()
			this.hideToast()
		})
		this.toastProgress = document.createElement('span')
		this.toastProgress.classList.add('mx-maxbet-warning-progress')
		this.toast.append(icon, this.toastText, this.toastOk, this.toastProgress)
		document.getElementById(BASE_ELEMENT_IDS.WRAPPER)?.appendChild(this.toast)
	}

	showToast(text: string, ms: number, blocking: boolean): void {
		this.hideToast()
		if (!this.toast) this.createToast()
		this.toastText.innerText = text
		this.toastOk.style.display = blocking ? '' : 'none'
		this.toast.classList.toggle('mx-maxbet-warning-blocking', blocking)
		this.placeToast()
		this.toastProgress.style.animation = 'none'
		void this.toastProgress.offsetWidth
		this.toastProgress.style.animation = ''
		this.toastProgress.style.animationDuration = `${ms}ms`
		this.toast.classList.add('mx-maxbet-warning-visible')
		this.toastTimeout = setTimeout(() => this.hideToast(), ms)
		if (blocking) {
			this.blocked = true
			this.block(true)
		}
	}

	placeToast(): void {
		const anchor = this.getAnchor?.()
		this.toast.classList.toggle('mx-maxbet-warning-anchored', !!anchor)
		if (!anchor) {
			this.toast.style.left = this.toast.style.top = ''
			return
		}
		const parent = this.toast.offsetParent?.getBoundingClientRect()
		this.toast.style.left = `${anchor.x - (parent?.left ?? 0)}px`
		this.toast.style.top = `${anchor.y - (parent?.top ?? 0)}px`
	}

	hideToast(): void {
		clearTimeout(this.toastTimeout)
		this.toastTimeout = undefined
		this.toast?.classList.remove('mx-maxbet-warning-visible')
		if (this.blocked) {
			this.blocked = false
			this.block(false)
		}
	}
}
